import { BillingTier } from "types/generated";

type PlanFeature = { infoTooltip?: string; negative?: boolean; text: string };

// Lists are ordered for PlanDetailsTile's 2-column grid, which fills row-major (left cell, then right):
// consecutive items form one row [left, right], so the rendered columns mirror the Figma layout.
const FREE_PLAN_FEATURES: PlanFeature[] = [
  { text: "2 Users + 1 API key" },
  { text: "Policy as Code engine based on OPA", negative: true },
  { text: "1 public worker" },
  { text: "Notifications", negative: true },
  { text: "SSO OIDC" },
  { text: "Private workers", negative: true },
];

const STARTER_PLAN_FEATURES: PlanFeature[] = [
  { text: "Notifications" },
  { text: "OIDC integrations" },
  { text: "Webhooks" },
  { text: "Drift detection", negative: true },
  { text: "Private module registry" },
  { text: "Private workers", negative: true },
];

const STARTER_PLUS_PLAN_FEATURES: PlanFeature[] = [
  { text: "Drift detection" },
  { text: "Blueprints", negative: true },
  { text: "Unlimited users" },
  { text: "Targeted replans", negative: true },
  { text: "1 private worker" },
  { text: "Advanced scheduling", negative: true },
];

const BUSINESS_PLAN_FEATURES: PlanFeature[] = [
  { text: "Drift detection" },
  { text: "Unlimited users" },
  { text: "Blueprints" },
  { text: "Targeted replans" },
  { text: "Templates" },
  { text: "Private provider registry" },
  { text: "Advanced scheduling" },
  { text: "Concurrent-VCS Connections", negative: true },
];

const ENTERPRISE_PLAN_FEATURES: PlanFeature[] = [
  { text: "Concurrent-VCS Connections" },
  { text: "Internal source control system", infoTooltip: "Unreachable over the internet" },
  { text: "Audit Trail" },
  { text: "Enhanced Onboarding Support" },
  { text: "SSO Integration with SAML 2.0" },
  { text: "Gold Support + CSM" },
  { text: "OIDC API Keys" },
  { text: "MFA" },
];

const ENTERPRISE_PLUS_PLAN_FEATURES: PlanFeature[] = [
  { text: "Self-hosted" },
  { text: "Gold Support + CSM + Executive Escalation" },
  { text: "FedRAMP" },
  { text: "Unmanaged Resources (coming soon)" },
  { text: "On-prem / Airgapped" },
];

const STRATEGIC_PLAN_FEATURES: PlanFeature[] = [
  { text: "Named TAM" },
  { text: "24/7 Escalation" },
  { text: "Single Tenant" },
];

// No feature-set diff V4 <-> V5, so a tier shares one list across both generations.
export const PLAN_FEATURES_BY_TIER: Partial<Record<BillingTier, PlanFeature[]>> = {
  [BillingTier.V4Free]: FREE_PLAN_FEATURES,
  [BillingTier.V5Free]: FREE_PLAN_FEATURES,
  [BillingTier.V4Starter]: STARTER_PLAN_FEATURES,
  [BillingTier.V5Starter]: STARTER_PLAN_FEATURES,
  [BillingTier.V4StarterPlus]: STARTER_PLUS_PLAN_FEATURES,
  [BillingTier.V5StarterPlus]: STARTER_PLUS_PLAN_FEATURES,
  [BillingTier.V5StarterPlus_2]: STARTER_PLUS_PLAN_FEATURES,
  [BillingTier.V4Business]: BUSINESS_PLAN_FEATURES,
  [BillingTier.V5Business]: BUSINESS_PLAN_FEATURES,
  [BillingTier.V4Enterprise]: ENTERPRISE_PLAN_FEATURES,
  [BillingTier.V5Enterprise]: ENTERPRISE_PLAN_FEATURES,
  [BillingTier.V5EnterprisePlus]: ENTERPRISE_PLUS_PLAN_FEATURES,
  [BillingTier.V5Strategic]: STRATEGIC_PLAN_FEATURES,
};

// Promo box shown under the current plan (Enterprise + only) — extras available on request.
export const PLAN_UPGRADE_BOX_BY_TIER: Partial<
  Record<BillingTier, { title: string; items: string[] }>
> = {
  [BillingTier.V5EnterprisePlus]: {
    title: "Available upon request:",
    items: ["100+ Workers", "Named TAM", "24/7 Escalation", "Single Tenant"],
  },
};
